import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { setContext, getCurrentScope } from '@sentry/react';
import styled, { css } from 'styled-components';
import { Position, LayoutT, type PostMessage } from '~/shared/types';
import getCurrentLayout from './utils/getCurrentLayout';
import cleanPath from '../../utils/cleanPath';
import { ROOT } from '../../constants/paths';
import Header, { HeaderAction } from '../../components/Header';
import Footer from '../../components/Footer';
import CookieBanner from '../../components/CookieBanner';
import useWidgetContext from '../../hooks/useWidgetContext';
import useCopy from '../../hooks/useCopy';
import usePathPrefix from '../../hooks/usePathPrefix';
import useFireTrackingEvent from '../../hooks/useFireTrackingEvent';
import useFrameSizeSelector from '../../hooks/useFrameSizeSelector';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import IngestParamsContext from '~/widget/context/IngestParamsContext';
import { isNil } from 'ramda';
import useMessaging from '~/widget/hooks/useMessaging';
import useStandaloneFlags from '~/widget/hooks/useStandaloneFlags';

export type Props = {};

const StandaloneLayout: React.FC<Props> = ({ ...rest }) => {
  const location = useLocation();
  const { slug } = useParams();
  const pathPrefix = usePathPrefix();
  const navigate = useNavigate();
  const fireTrackingEvent = useFireTrackingEvent();
  const { actions: actionLabels } = useCopy();
  const frameSizeSelector = useFrameSizeSelector();

  const context = useWidgetContext();
  const { positions } = context;
  const { singleApp, noTrack } = useStandaloneFlags();
  const ingestParams = useContext(IngestParamsContext);

  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const root = pathPrefix + ROOT;

  const layoutType = getCurrentLayout({
    isFullScreen,
    pathname: location.pathname,
    expanded: true,
  });

  const currentApp =
    slug && typeof slug === 'string' ? context.apps[slug] : null;

  useEffect(() => {
    setContext('widget-context', {
      ...context,
    });

    return () => {
      // When we unmount the layout we need to clear the scope
      // https://docs.sentry.io/platforms/javascript/guides/nextjs/enriching-events/context/#clearing-context
      getCurrentScope().clear();
    };
  }, [context]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && layoutType === 'fullscreen') {
        setIsFullScreen(false);
      }
    },
    [layoutType],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const cleanedPath = cleanPath(location.pathname);
    fireTrackingEvent({
      event: 'VirtualPageView',
      path: cleanedPath,
    });
  }, [location, fireTrackingEvent]);

  useEffect(() => {
    if (ingestParams)
      if (ingestParams && 'path' in ingestParams && isNil(currentApp)) {
        void navigate(`${pathPrefix}${ingestParams.path}${location.search}`);
      }
  }, [ingestParams]);

  const actions: Array<HeaderAction> = currentApp
    ? [
        {
          label: actionLabels.goBackToOverview,
          icon: 'arrow-left',
          onClick: () => {
            setIsFullScreen(false);
            navigate(`${root}${location.search}`);
          },
        },
      ]
    : [];

  const onClose = useCallback(() => {
    fireTrackingEvent({
      event: 'DismissWidget',
    });

    navigate(`${root}${location.search}`);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const onMessage = useCallback(
    (message: PostMessage) => {
      switch (message.type) {
        case 'open':
          let pathname = `${pathPrefix}${message.payload.path ?? ROOT}`;

          const matched = message.payload.path?.match(/^\/apps\/(.+)/);
          // If the app is not inside the app context its not enabled we we redirect to not found page
          if (matched && matched[1] && isNil(context.apps[matched[1]])) {
            pathname = `${pathPrefix}/not-found`;
          }

          const params = new URLSearchParams({
            queryParams: message.payload.queryParams ?? '',
          });
          navigate(`${pathname}?${params.toString()}`);

          fireTrackingEvent({
            event: 'OpenCommand',
            path: message.payload.path ?? ROOT,
            triggerType: message.payload.triggerType,
          });

          break;
        default:
          break;
      }
    },
    [fireTrackingEvent, pathPrefix, root],
  );

  useMessaging(onMessage);

  return (
    <Container ref={containerRef}>
      <Overlay
        {...rest}
        $layout={layoutType}
        $position={positions[frameSizeSelector]}
      >
        <WidgetContainer $layout={layoutType}>
          {!singleApp && (
            <Header
              appearance={layoutType}
              onClose={onClose}
              actions={actions}
            />
          )}
          <Inner
            id="inner-container"
            $bypassResizer={currentApp ? currentApp.bypassResizer : false}
          >
            <Outlet />
          </Inner>
          {!singleApp && <Footer dataTestId="layout-footer" />}
          {!noTrack && <CookieBanner />}
        </WidgetContainer>
      </Overlay>
    </Container>
  );
};

const Container = styled.div(
  () => css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;

    display: flex;
    justify-content: flex-start;
    align-items: flex-start;

    width: 100%;
    height: 100%;
  `,
);

const Overlay = styled.div<{
  $layout: LayoutT;
  $position: Position;
}>(
  () => css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-end;
    align-self: flex-start;
  `,
);

export const scrollBarStyles = css`
  &::-webkit-scrollbar {
    width: 0.5em;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.color('secondary')};
    border-radius: ${({ theme }) => theme.getSystem().border.radius.base};
    outline: none;
  }
`;

const WidgetContainer = styled.div<{ $layout: LayoutT }>(
  () => css`
    /* This is used to forbid overriding widget with the forced dark mode */
    color-scheme: only light;
    background-color: #fff;

    position: relative;

    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    margin: 0 auto;
  `,
);

const Inner = styled.div<{ $bypassResizer?: boolean }>(
  ({ $bypassResizer }) => css`
    height: 100%;
    width: 100%;

    overflow-y: ${$bypassResizer ? 'auto' : 'scroll'};

    ${scrollBarStyles}
  `,
);

export default StandaloneLayout;
